import { createFileRoute, useNavigate } from '@tanstack/react-router';

import { AuthenticatedMobileShell } from '../components/AuthenticatedMobileShell.tsx';
import { InvoiceEditor } from '../components/InvoiceEditor.tsx';
import { StatusMessage } from '../components/StatusMessage.tsx';
import { useMe } from '../lib/auth.ts';
import { useCounterparties } from '../lib/counterparties.ts';
import { useCreateInvoice } from '../lib/invoices.ts';
import { useSettings } from '../lib/settings.ts';
import type { InvoiceDetailResponse, UpsertInvoiceInput } from '../../shared/invoice.ts';
import { CenteredMessage, LoginScreen } from './counterparties.tsx';

export const Route = createFileRoute('/invoices/new')({
  component: NewInvoiceRoute,
});

export function NewInvoiceRoute(): React.ReactElement {
  const me = useMe();

  if (me.isLoading) {
    return <CenteredMessage>Loading…</CenteredMessage>;
  }

  if (me.data == null) {
    return <LoginScreen />;
  }

  return (
    <AuthenticatedMobileShell currentPath="/invoices/new">
      <AuthenticatedNewInvoice />
    </AuthenticatedMobileShell>
  );
}

function AuthenticatedNewInvoice(): React.ReactElement {
  const navigate = useNavigate();
  const settings = useSettings();
  const customers = useCounterparties('');
  const createInvoice = useCreateInvoice();

  if (settings.isPending || customers.isPending) {
    return <InvoiceEditorShell title="New invoice">Loading…</InvoiceEditorShell>;
  }

  if (settings.isError || customers.isError) {
    return (
      <InvoiceEditorShell title="New invoice">
        <StatusMessage tone="error">
          Could not load invoice editor. Try again or check the logs.
        </StatusMessage>
      </InvoiceEditorShell>
    );
  }

  async function save(input: UpsertInvoiceInput): Promise<InvoiceDetailResponse> {
    const response = await createInvoice.mutateAsync(input);
    await navigate({
      to: '/invoices/$id',
      params: { id: response.invoice.id },
      search: { saved: response.invoice.number },
      replace: true,
    });
    return response;
  }

  async function saveAndClose(input: UpsertInvoiceInput): Promise<InvoiceDetailResponse> {
    const response = await createInvoice.mutateAsync(input);
    await navigate({ to: '/' as never });
    return response;
  }

  return (
    <InvoiceEditor
      mode="create"
      settings={settings.data}
      customers={customers.data.counterparties}
      onSave={save}
      onSaveAndClose={saveAndClose}
    />
  );
}

function InvoiceEditorShell({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <div className="min-h-screen">
      <header className="border-b border-line bg-paper-soft/60">
        <div className="mx-auto max-w-6xl px-4 py-5 sm:px-6">
          <h1 className="font-serif text-2xl italic text-ink">{title}</h1>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-4 py-8 text-sm text-ink-soft sm:px-6">{children}</main>
    </div>
  );
}
